import { useState, useEffect } from "react";
import { Sun, Moon, Menu, X, Globe } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import { useLanguage } from "@/hooks/use-language";

const LANGS = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "pt", label: "Português" },
  { code: "sv", label: "Svenska" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

export const scrollTo = (hash) => {
  if (!document.querySelector(hash)) {
    window.location.href = `/${hash}`;
    return;
  }
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(hash)?.scrollIntoView();
  }
};

export const Header = () => {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem("s360-fs") || 0));

  useEffect(() => {
    document.documentElement.style.fontSize = ["16px", "18px", "20px"][fontSize];
    localStorage.setItem("s360-fs", String(fontSize));
  }, [fontSize]);

  const NAV = [
    { label: t.nav.platform, href: "#platform" },
    { label: t.nav.architecture, href: "#architecture" },
    { label: t.nav.benchmark, href: "#benchmark" },
    { label: t.nav.security, href: "#security" },
    { label: t.nav.team, href: "#team" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 glass-header border-b ${
        scrolled
          ? "bg-background/85 dark:bg-[#0a0a0a]/80 border-border"
          : "bg-background/60 dark:bg-[#0a0a0a]/50 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          data-testid="header-logo"
          className="flex items-center"
        >
          <img
            src="/logo-transparent.webp"
            alt="Streamline360"
            className="h-8 w-auto"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8" data-testid="header-nav">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => go(e, item.href)}
              data-testid={`nav-${item.href.slice(1)}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 font-medium"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="language-toggle"
              aria-label="Change language"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-teal-500/60 transition-colors duration-300"
            >
              <Globe size={16} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-12 w-40 max-h-72 overflow-y-auto border border-border bg-popover shadow-xl z-50" data-testid="language-menu">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-${l.code}`}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${
                      lang === l.code ? "text-teal-600 dark:text-teal-400 font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center border border-border rounded-full overflow-hidden" data-testid="font-size-control" role="group" aria-label="Text size">
            {["A−", "A", "A+"].map((label, i) => (
              <button
                key={label}
                onClick={() => setFontSize(i)}
                data-testid={`font-size-btn-${i}`}
                aria-label={`Text size ${label}`}
                className={`px-2.5 h-10 text-xs font-display font-bold transition-colors duration-200 ${
                  fontSize === i ? "text-teal-600 dark:text-teal-400 bg-secondary/70" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle dark / light mode"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-teal-500/60 transition-colors duration-300"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="header-demo-cta"
            className="hidden md:inline-flex items-center h-10 px-6 rounded-full bg-gradient-to-r from-teal-500 to-green-500 text-[#0a0a0a] text-sm font-semibold hover:opacity-90 hover:scale-[1.03] transition-[opacity,transform] duration-300"
          >
            {t.bookDemo}
          </a>
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 dark:bg-[#0a0a0a]/95 glass-header" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => go(e, item.href)}
                className="text-base font-medium text-foreground"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              className="inline-flex items-center justify-center h-11 rounded-full bg-gradient-to-r from-teal-500 to-green-500 text-[#0a0a0a] text-sm font-semibold"
            >
              {t.bookDemo}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
